import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { cn } from "@/modules/lib/utils"
import { getYouTubeVideoIdFromUrl, isYoutubeUrl } from "@modules/ui/components/richText/richTextUtils"
import { useWindowSize } from "@modules/utils/hooks/useWindowsSize"
import { EditorContent, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { useEffect, useState } from "react"
import styled, { css } from "styled-components"

type HURichTextViewProps = {
  value: string
  paragrapheFontStyle: "BL" | "BM" | "BS" | "BXS" | "TL"
  numberOfLine?: number
} & React.ComponentProps<"label">

export function HURichTextView({
  value = "",
  paragrapheFontStyle,
  numberOfLine,
  className = "",
  ...props
}: HURichTextViewProps) {
  const [shownVideoUrl, setShownVideoUrl] = useState<string>()
  const [showVideoDialog, setShowVideoDialog] = useState<boolean>(false)
  const { width } = useWindowSize()
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    editable: false,
  })

  useEffect(() => {
    if (editor && editor.commands) editor.commands.setContent(value)
  }, [value, editor?.commands])

  const videoWidth = width * 0.8
  const videoHeight = (videoWidth * 9) / 16

  return (
    <Component $numberOfLine={numberOfLine} {...props}>
      <EditorContent
        editor={editor}
        className={"hu-richtext-view " + paragrapheFontStyle + " " + className}
        style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
        readOnly={true}
        onClick={(e) => {
          const link = (e.target as HTMLElement).closest("a")
          if (link && isYoutubeUrl(link.href)) {
            e.preventDefault()
            const videoId = getYouTubeVideoIdFromUrl(link.href)
            if (videoId) {
              const embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0?version=3&autoplay=1&autopause=0&muted=1&controls=1`
              setShowVideoDialog(true)
              setShownVideoUrl(embedUrl)
            }
            e.stopPropagation()
          }
        }}
      />
      <Dialog
        open={showVideoDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowVideoDialog(false)
          }
        }}
      >
        <DialogContent
          className={cn("z-50 min-w-0 overflow-hidden rounded-md p-0")}
          style={{
            width: videoWidth,
            height: videoHeight,
            minWidth: videoWidth,
            padding: 0,
          }}
          autoFocus
        >
          <DialogHeader className="sr-only">
            <DialogTitle className="sr-only">Vidéo player</DialogTitle>
          </DialogHeader>
          <iframe
            src={shownVideoUrl}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Embedded youtube"
            width="100%"
            height="100%"
            style={{ border: 0, zIndex: 1106 }}
          />
        </DialogContent>
      </Dialog>
    </Component>
  )
}

const Component = styled.label<{
  $numberOfLine?: number
}>`
  ${({ $numberOfLine }) =>
    $numberOfLine &&
    css`
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: ${$numberOfLine};
      overflow: hidden;
    `}
`
